import React, {useEffect, useRef, useState} from 'react';
import I18n from 'app/i18n';

interface AnimatedParticipantsCountProps {
    value: number;
}

interface CountAnimationParams {
    from: number;
    to: number;
    setDisplayValue: (value: number) => void;
    frameRef: React.MutableRefObject<number | null>;
}

const MIN_ANIMATION_MS = 400;
const MAX_ANIMATION_MS = 2000;
const MS_PER_STEP = 100;
const EASE_OUT_POWER = 3;

const easeOutCubic = (progress: number): number => 1 - (1 - progress) ** EASE_OUT_POWER;

const cancelFrame = (frameRef: React.MutableRefObject<number | null>): void => {
    if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
    }
};

const startCountAnimation = ({
    from,
    to,
    setDisplayValue,
    frameRef
}: CountAnimationParams): (() => void) => {
    const delta = to - from;
    const duration = Math.min(MAX_ANIMATION_MS, Math.max(MIN_ANIMATION_MS, delta * MS_PER_STEP));
    const startedAt = performance.now();

    const tick = (now: number): void => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const nextValue = Math.round(from + (to - from) * easeOutCubic(progress));

        setDisplayValue(nextValue);

        if (progress < 1) {
            frameRef.current = requestAnimationFrame(tick);
        }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => cancelFrame(frameRef);
};

const AnimatedParticipantsCount = ({value}: AnimatedParticipantsCountProps): React.ReactElement => {
    const [displayValue, setDisplayValue] = useState(value);
    const displayValueRef = useRef(value);
    const frameRef = useRef<number | null>(null);

    displayValueRef.current = displayValue;

    useEffect(() => {
        const from = displayValueRef.current;

        if (from === value || value < from) {
            setDisplayValue(value);
            return () => cancelFrame(frameRef);
        }

        return startCountAnimation({
            frameRef,
            from,
            setDisplayValue,
            to: value
        });
    }, [value]);

    return <span>{I18n.t('participants_count', {count: displayValue})}</span>;
};

export default AnimatedParticipantsCount;
